import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, ModalContent, ModalHeader } from '@forge/common';
import { useTranslation } from 'react-i18next';
import { IFilterOptions } from '../../Redux/activityModels';
import { getStatusLabelKey, translateWorkflowTaskDescription, isNonEmptyString, sanitizeById } from '../../utils';
import ActivityFilterBody from './ActivityFilterBody';
import ActivityFilterFooter from './ActivityFilterFooter';

export interface IFilterSelections {
  statuses: string[];
  activity: string[];
  siteIds: string[];
  userIds: string[];
}

export interface ActivityFilterModalProps {
  filterOptions: IFilterOptions | null;
  isLoading: boolean;
  error: string | null;
  selectedStatuses: string[];
  selectedActivity: string[];
  selectedSiteIds: string[];
  selectedUserIds: string[];
  userOptions: { id: string; label: string }[];
  onClose: () => void;
  onApply: (selections: IFilterSelections) => void;
  onRefresh: () => void;
}

function toggleItem(prev: string[], id: string): string[] {
  return prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
}

function toggleSelectAll(prev: string[], allIds: string[]): string[] {
  return prev.length === allIds.length ? [] : [...allIds];
}

const ActivityFilterModal: React.FC<ActivityFilterModalProps> = ({
  filterOptions,
  isLoading,
  error,
  selectedStatuses,
  selectedActivity,
  selectedSiteIds,
  selectedUserIds,
  userOptions: rawUserOptions,
  onClose,
  onApply,
  onRefresh,
}) => {
  const [localStatuses, setLocalStatuses] = useState<string[]>(selectedStatuses);
  const [localActivity, setLocalActivity] = useState<string[]>(selectedActivity);
  const [localSiteIds, setLocalSiteIds] = useState<string[]>(selectedSiteIds);
  const [localUserIds, setLocalUserIds] = useState<string[]>(selectedUserIds);

  // Sanitize filter options: drop null/empty/invalid entries and dedupe by id
  // so the UI never renders blank rows and select-all/apply never operate on
  // unusable ids.
  const statuses = useMemo(
    () => Array.from(new Set((filterOptions?.statuses ?? []).filter(isNonEmptyString))),
    [filterOptions],
  );
  const activities = useMemo(() => sanitizeById(filterOptions?.activities, a => a.description), [filterOptions]);
  const sites = useMemo(() => sanitizeById(filterOptions?.sites, s => s.name), [filterOptions]);
  const userOptions = useMemo(() => sanitizeById(rawUserOptions, u => u.label), [rawUserOptions]);

  useEffect(() => {
    if (filterOptions) {
      setLocalStatuses(prev => prev.filter(s => statuses.includes(s)));
    }
  }, [filterOptions, statuses]);

  useEffect(() => {
    if (filterOptions) {
      const validIds = activities.map(a => a.id);
      setLocalActivity(prev => prev.filter(id => validIds.includes(id)));
    }
  }, [filterOptions, activities]);

  useEffect(() => {
    if (filterOptions) {
      const validIds = sites.map(s => s.id);
      setLocalSiteIds(prev => prev.filter(id => validIds.includes(id)));
    }
  }, [filterOptions, sites]);

  useEffect(() => {
    const validIds = userOptions.map(u => u.id);
    setLocalUserIds(prev => prev.filter(id => validIds.includes(id)));
  }, [userOptions]);

  const { t } = useTranslation();

  const handleApply = useCallback(() => {
    onApply({ statuses: localStatuses, activity: localActivity, siteIds: localSiteIds, userIds: localUserIds });
  }, [onApply, localStatuses, localActivity, localSiteIds, localUserIds]);

  const handleReset = useCallback(() => {
    setLocalStatuses([]);
    setLocalActivity([]);
    setLocalSiteIds([]);
    setLocalUserIds([]);
  }, []);

  const handleStatusToggle = useCallback((id: string) => {
    setLocalStatuses(prev => toggleItem(prev, id));
  }, []);

  const handleActivityToggle = useCallback((id: string) => {
    setLocalActivity(prev => toggleItem(prev, id));
  }, []);

  const handleSiteToggle = useCallback((id: string) => {
    setLocalSiteIds(prev => toggleItem(prev, id));
  }, []);

  const handleUserToggle = useCallback((id: string) => {
    setLocalUserIds(prev => toggleItem(prev, id));
  }, []);

  const allStatusIds = statuses;
  const allActivityIds = useMemo(() => activities.map(a => a.id), [activities]);
  const allSiteIds = useMemo(() => sites.map(s => s.id), [sites]);
  const allUserIds = useMemo(() => userOptions.map(u => u.id), [userOptions]);

  const statusOptions = useMemo(
    () => statuses.map(s => ({ id: s, label: t(getStatusLabelKey(s)) })),
    [statuses, t],
  );
  const activityOptions = useMemo(
    () => activities.map(a => ({ id: a.id, label: translateWorkflowTaskDescription(a.description, t) })),
    [activities, t],
  );
  const siteOptions = useMemo(
    () => sites.map(s => ({ id: s.id, label: s.name })),
    [sites],
  );

  const handleSelectAllStatuses = useCallback((targetIds?: string[]) => {
    const ids = targetIds ?? allStatusIds;
    setLocalStatuses(prev => toggleSelectAll(prev, ids));
  }, [allStatusIds]);

  const handleSelectAllActivity = useCallback((targetIds?: string[]) => {
    const ids = targetIds ?? allActivityIds;
    setLocalActivity(prev => toggleSelectAll(prev, ids));
  }, [allActivityIds]);

  const handleSelectAllSites = useCallback((targetIds?: string[]) => {
    const ids = targetIds ?? allSiteIds;
    setLocalSiteIds(prev => toggleSelectAll(prev, ids));
  }, [allSiteIds]);

  const handleSelectAllUsers = useCallback((targetIds?: string[]) => {
    const ids = targetIds ?? allUserIds;
    setLocalUserIds(prev => toggleSelectAll(prev, ids));
  }, [allUserIds]);

  const sections = [
    {
      id: 'filter-status',
      title: t('activityLogFilterStatus'),
      groupId: 'status',
      options: statusOptions,
      selected: localStatuses,
      onToggle: handleStatusToggle,
      onSelectAll: handleSelectAllStatuses,
    },
    {
      id: 'filter-activity',
      title: t('activityLogFilterActivity'),
      groupId: 'activity',
      options: activityOptions,
      selected: localActivity,
      onToggle: handleActivityToggle,
      onSelectAll: handleSelectAllActivity,
      enableInfiniteScroll: true,
    },
    {
      id: 'filter-site',
      title: t('activityLogFilterSite'),
      groupId: 'site',
      options: siteOptions,
      selected: localSiteIds,
      onToggle: handleSiteToggle,
      onSelectAll: handleSelectAllSites,
      enableInfiniteScroll: true,
    },
    {
      id: 'filter-user',
      title: t('activityLogFilterCreatedBy'),
      groupId: 'user',
      options: userOptions,
      selected: localUserIds,
      onToggle: handleUserToggle,
      onSelectAll: handleSelectAllUsers,
    },
  ];

  return (
    <Modal
      className="activity-filter-modal"
      onModalClose={onClose}
      footerRight={<ActivityFilterFooter isLoading={isLoading} error={error} onReset={handleReset} onApply={handleApply} />}
    >
      <ModalHeader title={t('activityLogFilterTitle')} onModalClose={onClose} />
      <ModalContent>
        <hr className="activity-filter-header-divider" />
        <ActivityFilterBody
          isLoading={isLoading}
          error={error}
          onRefresh={onRefresh}
          sections={sections}
        />
      </ModalContent>
    </Modal>
  );
};

export default ActivityFilterModal;
